import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, CheckSquare, FileSignature } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ESignatureModal, type ESignaturePayload } from '@/components/esignature/ESignatureModal';
import { formatDate, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

// TODO(psur): schema-driven editor per section. For MVP we render JSON per section.

const SECTIONS: { key: string; label: string }[] = [
  { key: '1', label: '1. Introduction' },
  { key: '2', label: '2. Worldwide marketing auth status' },
  { key: '3', label: '3. Actions taken in the reporting interval for safety' },
  { key: '4', label: '4. Changes to reference safety information' },
  { key: '5', label: '5. Estimated exposure and use patterns' },
  { key: '6', label: '6. Data in summary tabulations' },
  { key: '7', label: '7. Summaries of significant findings from clinical trials' },
  { key: '8', label: '8. Findings from non-interventional studies' },
  { key: '9', label: '9. Information from other clinical trials and sources' },
  { key: '10', label: '10. Non-clinical data' },
  { key: '11', label: '11. Literature' },
  { key: '12', label: '12. Other periodic reports' },
  { key: '13', label: '13. Lack of efficacy in controlled clinical trials' },
  { key: '14', label: '14. Late-breaking information' },
  { key: '15', label: '15. Overview of signals' },
  { key: '16', label: '16. Signal and risk evaluation' },
  { key: '17', label: '17. Benefit evaluation' },
  { key: '18', label: '18. Integrated benefit-risk analysis' },
  { key: '19', label: '19. Conclusions and actions' },
];

interface PsurDetail {
  id: string;
  product: string | null;
  reportNumber: string | null;
  interval: string | null;
  dlp: string | null;
  dueAt: string | null;
  status: string | null;
  content: Record<string, unknown> | null;
}

export function PsurDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canEdit = user && ROLE_RANK[user.role] >= ROLE_RANK['REVIEWER'];
  const canApprove = user && ROLE_RANK[user.role] >= ROLE_RANK['MANAGER'];

  const [active, setActive] = useState('1');
  const [draft, setDraft] = useState<string>('');
  const [esignOpen, setEsignOpen] = useState(false);

  const psur = useQuery({
    queryKey: ['psur', id],
    queryFn: async () => {
      try {
        return (await api.get(`/api/psur-draft/${id}`)).data as PsurDetail;
      } catch {
        return null;
      }
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(draft);
      } catch {
        throw new Error('Invalid JSON');
      }
      return api.patch(`/api/psur-draft/${id}`, { section: active, content: parsed });
    },
    onSuccess: () => {
      toast.success('Saved', `Section ${active}`);
      qc.invalidateQueries({ queryKey: ['psur', id] });
    },
    onError: (err) => toast.error('Save failed', extractApiError(err).message),
  });

  const qcComplete = useMutation({
    mutationFn: async () => api.post(`/api/psur-draft/${id}/qc-complete`, {}),
    onSuccess: () => {
      toast.success('QC marked complete');
      qc.invalidateQueries({ queryKey: ['psur', id] });
    },
    onError: (err) => toast.error('QC failed', extractApiError(err).message),
  });

  const submitPsur = useMutation({
    mutationFn: async (sig: ESignaturePayload) =>
      api.post(`/api/psur-draft/${id}/submit`, {
        esignature: {
          password: sig.password,
          reason: `${sig.reason}: ${sig.intent}`,
        },
      }),
    onSuccess: () => {
      toast.success('PSUR submitted', 'E-signature recorded');
      setEsignOpen(false);
      qc.invalidateQueries({ queryKey: ['psur', id] });
    },
    onError: (err) => toast.error('Submit failed', extractApiError(err).message),
  });

  if (psur.isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (!psur.data) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/psur">
            <ArrowLeft className="mr-2 h-4 w-4" /> PSURs
          </Link>
        </Button>
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            PSUR not found or backend endpoint not yet available.
          </CardContent>
        </Card>
      </div>
    );
  }

  const p = psur.data;
  const content = (p.content ?? {}) as Record<string, unknown>;
  const activeValue = content[active];
  const activeText = activeValue != null ? JSON.stringify(activeValue, null, 2) : '{}';

  function onSelect(k: string) {
    setActive(k);
    const v = content[k];
    setDraft(v != null ? JSON.stringify(v, null, 2) : '{}');
  }

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/psur">
            <ArrowLeft className="mr-2 h-4 w-4" /> PSURs
          </Link>
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{p.product ?? 'PSUR'}</h1>
            <p className="text-sm text-muted-foreground">
              {p.reportNumber ?? '-'} - {p.interval ?? '-'} - DLP{' '}
              {p.dlp ? formatDate(p.dlp) : '-'}
            </p>
          </div>
          <Badge variant={p.status === 'SUBMITTED' || p.status === 'APPROVED' ? 'success' : 'warning'}>
            {p.status ?? 'DRAFT'}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[260px_1fr]">
        <aside className="rounded-md border bg-card p-2">
          <div className="mb-2 px-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            ICH E2C(R2) sections
          </div>
          <ul className="space-y-0.5">
            {SECTIONS.map((s) => (
              <li key={s.key}>
                <button
                  onClick={() => onSelect(s.key)}
                  className={cn(
                    'w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                    active === s.key
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                  )}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {SECTIONS.find((x) => x.key === active)?.label}
            </CardTitle>
            <CardDescription>JSON editor - TODO: schema-driven UI.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              rows={20}
              className="font-mono text-xs"
              value={draft || activeText}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="{}"
            />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => save.mutate()} disabled={!canEdit || save.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {save.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => qcComplete.mutate()}
                disabled={!canEdit || qcComplete.isPending}
              >
                <CheckSquare className="mr-1.5 h-3.5 w-3.5" />
                {qcComplete.isPending ? 'Marking...' : 'QC complete'}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setEsignOpen(true)}
                disabled={!canApprove || p.status === 'SUBMITTED'}
              >
                <FileSignature className="mr-1.5 h-3.5 w-3.5" /> Submit (e-sign)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <ESignatureModal
        open={esignOpen}
        onOpenChange={setEsignOpen}
        title="Submit PSUR"
        description={`Submitting ${p.product ?? 'PSUR'} (${p.reportNumber ?? '-'}) to the regulatory authority. This action is final.`}
        reasonPresets={[
          { value: 'submission', label: 'Submission' },
          { value: 'approval', label: 'Approval' },
        ]}
        submitLabel="Sign and submit"
        isSubmitting={submitPsur.isPending}
        onConfirm={async (sig) => {
          await submitPsur.mutateAsync(sig);
        }}
      />
    </div>
  );
}
